import {
  Account,
  Client,
  Databases,
  ID,
  Models,
  OAuthProvider,
} from 'appwrite';

import {
  getSessionStateFromCookie,
  setSessionStateCookie,
} from '@/utils/getSessionStateFromCookie';

import {
  clearSession,
  isSessionChecked,
  isSessionCheckedIsFalse,
  setSessionValid,
} from '../../components/shared/AppWriteLocalStroage';
import {
  getLoginToken,
  setToken,
  // setUserId,
  // setUserName,
  // setZl_UID,
} from '../../components/shared/getToken';
import { resetSessionExistsCookie } from '../../components/shared/ManageTokenForClient/manageCookies';
import { notify } from '../../components/shared/notify';
// import { getUserProfile } from '../../services/user';
import { ICreateAccount, ILogin } from '../../utils/interfaces/appwrite';

const appwriteClient = new Client();

const appwriteProjectId = process.env.NEXT_PUBLIC_APPWRITE_ID || '';
const appwriteUrl = process.env.NEXT_PUBLIC_APPWRITE_URL || '';
const appwriteDevKey = process.env.NEXT_PUBLIC_APPWRITE_DEV_KEY || '';
const FIFTEEN_MINUTES =
  +(process.env.NEXT_PUBLIC_SSO_SIGNUP_TIME_MINUTES || '15') * 60 * 1000;

let sessionReadPromise: Promise<any | null> | null = null;
let cachedPrefs: Record<string, any> | null = null;

appwriteClient.setEndpoint(appwriteUrl).setProject(appwriteProjectId);

// Bypass rate limits in development/staging using an Appwrite Dev Key.
// See: https://appwrite.io/docs/advanced/security/rate-limits#dev-keys
// Never set NEXT_PUBLIC_APPWRITE_DEV_KEY in production.
if (appwriteDevKey) {
  appwriteClient.setDevKey(appwriteDevKey);
}

const account = new Account(appwriteClient);

const databases = new Databases(appwriteClient);

const createUserAccount = async ({ email, password, name }: ICreateAccount) => {
  return await account.create(ID.unique(), email, password, name);
};

const getUserLogs = async (): Promise<Models.LogList> => {
  return await account.listLogs();
};

const loginWithGoogle = async (isRegister?: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set('googleLoginCompleted', 'true');
  currentUrl.searchParams.set('googleSSOSignup', 'true');

  if (isRegister) {
    currentUrl.searchParams.set('isRegister', isRegister);
  }

  await logoutIfSessionExists();

  return await account.createOAuth2Session(
    OAuthProvider.Google,
    currentUrl.toString(),
    `${process.env.NEXT_PUBLIC_API_URL_APP_URL}/auth/login/`,
    // [
    //   'https://www.googleapis.com/auth/youtube.readonly',
    //   'https://www.googleapis.com/auth/youtube.force-ssl',
    // ],
  );
};

const loginWithGoogleOneTapToken = async (userId: string, secret: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  await logoutIfSessionExists();

  return await account.createSession(userId, secret);
};

const checkIsSignup = async () => {
  const user = await account.get();
  const session = await getCurrentSessionOnce();

  const userCreatedAt = new Date(user?.$createdAt).getTime();
  const sessionCreatedAt = new Date(session?.$createdAt).getTime();

  const isSignup = Math.abs(sessionCreatedAt - userCreatedAt) < FIFTEEN_MINUTES;

  return { user, session, isSignup };
};

const createTarget = async ({
  target_id,
  identifier,
  provider_id,
}: {
  target_id: string;
  identifier: string;
  provider_id: string;
}) => {
  try {
    return await account.createPushTarget(target_id, identifier, provider_id);
  } catch (e: any) {
    if (e.code === 409) {
      return await appwriteService.updateTarget({
        target_id,
        identifier,
      });
    } else {
      throw e;
    }
  }
};

const updateTarget = async ({
  target_id,
  identifier,
}: {
  target_id: string;
  identifier: string;
}) => {
  return await account.updatePushTarget(target_id, identifier);
};

export const updateOnboardingPrefs = async (prefs: Record<string, any>) => {
  if (!cachedPrefs) {
    cachedPrefs = await account.getPrefs();
  }

  cachedPrefs = { ...cachedPrefs, ...prefs };

  return account.updatePrefs(cachedPrefs);
};
const deleteTarget = async ({ target_id }: { target_id: string }) => {
  return await account.deletePushTarget(target_id);
};

const updatePrefs = async ({ data }: { data: any }) => {
  return await account.updatePrefs(data);
};

const sendVerificationEmail = async () => {
  return await account.createVerification(
    `${process.env.NEXT_PUBLIC_API_URL_APP_URL}`,
  );
};

const verifyVerificationEmail = async (userId: string, secret: string) => {
  return await account.updateVerification(userId, secret);
};

const handleRedirection = () => {
  if (typeof window !== undefined) {
    notify('You are not authenticated', 'error');

    const redirectUrl = decodeURIComponent(window.location.pathname);
    window.location.href = `/auth/login?redirectUrl=${redirectUrl}`;
  }
};

const loginWithApple = async (isRegister?: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set('appleLoginCompleted', 'true');
  if (isRegister) {
    currentUrl.searchParams.set('isRegister', isRegister);
  }

  await logoutIfSessionExists();

  return await account.createOAuth2Session(
    OAuthProvider.Apple,
    currentUrl.toString(),
    `${process.env.NEXT_PUBLIC_API_URL_APP_URL}/auth/login/`,
  );
};
// const setUpdatedProfile = async () => {
//   try {
//     const data = await getUserProfile();
//     await setUserId(data?.data?.id);
//     await setZl_UID(data?.data?.zl_uid);
//     await setUserName(data?.data?.name);
//   } catch (error: any) {
//     if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
//       console.error('Error initializing messaging:', error);
//     }
//   }
// };

export const setTokenInCookies = async () => {
  try {
    const tokenResponse = await account.createJWT();
    const token = tokenResponse.jwt;
    await setToken(token);
    // await setUpdatedProfile();
    return token;
  } catch (e: any) {
    if (e.code === 401 && e.type === 'general_unauthorized_scope') {
      handleRedirection();
      resetSessionExistsCookie();
      clearSession();
      return '';
    } else if (e.code !== 401 || e.type !== 'general_unauthorized_scope') {
      {
        throw e;
      }
    }
  }
  return '';
};

const logoutIfSessionExists = async () => {
  try {
    const user = await getCurrentSessionOnce();
    if (user) {
      return await logout();
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('user_id_iframe');
    }
  } catch (e: any) {
    if (e.code === 401 && e.type === 'general_unauthorized_scope') {
      return null;
    } else if (e.code !== 401 || e.type !== 'general_unauthorized_scope') {
      {
        throw e;
      }
    }
  }
  return null;
};

export const getCurrentSessionOnce = async () => {
  if (getSessionStateFromCookie() === 'logged_out') {
    return null;
  }
  if (!sessionReadPromise) {
    sessionReadPromise = account
      .getSession('current')
      .then((session) => {
        setSessionStateCookie('logged_in');
        return session;
      })
      .catch((e: any) => {
        if (e.code === 401) {
          setSessionStateCookie('logged_out');
          return null;
        }
        throw e;
      })
      .finally(() => {
        setTimeout(() => {
          sessionReadPromise = null;
        }, 1000);
      });
  }

  return sessionReadPromise;
};

export const checkSessionExistsRaw = async () => {
  try {
    return await getCurrentSessionOnce();
  } catch (e: any) {
    if (e.code === 401 && e.type === 'general_unauthorized_scope') {
      return null;
    } else if (e.code !== 401 || e.type !== 'general_unauthorized_scope') {
      {
        throw e;
      }
    }
    return null;
  }
};

const createSessionBySecretKey = async ({
  user_id,
  secret,
}: {
  user_id: string;
  secret: string;
}) => {
  await logoutIfSessionExists();
  return await account.createSession(user_id, secret);
};

const login = async ({ email, password }: ILogin) => {
  setSessionStateCookie('logged_in');
  return await account.createEmailPasswordSession(email, password);
};

const createAnonymousSession = async () => {
  return await account.createAnonymousSession();
};

const isLoggedIn = async () => {
  if (isSessionChecked()) {
    // Also verify auth_token is present and not expired.
    // sessionChecked lasts 60 days but auth_token only lasts 13 minutes.
    // If the token is gone (tab was closed, token expired), re-validate with
    // Appwrite and refresh the token so API calls continue to work.
    const token = await getLoginToken();
    if (token) {
      return true;
    }
    // Token missing/expired — fall through to re-validate the Appwrite session.
  } else if (isSessionCheckedIsFalse()) {
    return false;
  }

  try {
    const session = await getCurrentSessionOnce();
    if (session) {
      setSessionValid(true);
      // Refresh the auth_token so subsequent API calls work.
      await setTokenInCookies();
      return true;
    }
  } catch {
    setSessionValid(false);
    return false;
  }
  return false;
};

const getGoogleAccessToken = async (): Promise<string | null> => {
  try {
    // Fetch the current session
    const session = await getCurrentSessionOnce();

    // Ensure the session is for Google
    if (session.provider === 'google' && session.providerAccessToken) {
      return session.providerAccessToken;
    }

    console.error('Google session or access token not found');
    return null;
  } catch (error) {
    console.error('Error retrieving session:', error);
    return null;
  }
};

const getCurrentUser = async () => {
  try {
    const session = await account.getSession('current');
    setSessionValid(true);
    return session;
  } catch (e: any) {
    if (e.code === 401 && e.type === 'general_unauthorized_scope') {
      return null;
    } else if (e.code !== 401 || e.type !== 'general_unauthorized_scope') {
      {
        throw e;
      }
    }
    return null;
  }
};

export const logout = async () => {
  return await account.deleteSession('current');
};

export const getUserAccount: any = async () => {
  return await account.get();
};

const passwordRecovery = async (email: string) => {
  return await account.createRecovery(
    email,
    `${process.env.NEXT_PUBLIC_API_URL_APP_URL}/auth/reset-password/`,
  );
};

const updateName = async (name: string) => {
  return await account.updateName(name);
};

const updatePhone = async (phone: string, password: string) => {
  return await account.updatePhone(phone, password);
};

const updatePassword = async (password: string, oldPassword: string) => {
  return await account.updatePassword(password, oldPassword);
};

const passwordRecoveryConfirmation = async ({
  userId,
  secret_code,
  password,
}: {
  userId: string;
  secret_code: string;
  password: string;
}) => {
  return await account.updateRecovery(userId, secret_code, password);
};

const appwriteService: any = {
  createUserAccount,
  getUserLogs,
  login,
  isLoggedIn,
  getCurrentUser,
  logout,
  createSessionBySecretKey,
  loginWithGoogle,
  loginWithGoogleOneTapToken,
  setTokenInCookies,
  account,
  loginWithApple,
  passwordRecovery,
  passwordRecoveryConfirmation,
  logoutIfSessionExists,
  createAnonymousSession,
  updateName,
  updatePhone,
  getUserAccount,
  updatePassword,
  createTarget,
  updateTarget,
  deleteTarget,
  updatePrefs,
  getGoogleAccessToken,
  updateOnboardingPrefs,
  sendVerificationEmail,
  verifyVerificationEmail,
  databases,
  checkIsSignup,
};

export default appwriteService;
